import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Select,
    DatePicker,
    Button,
    Upload,
    Divider,
    Avatar,
    Breadcrumb,
} from 'antd';
import {
    ChevronDown,
    ChevronRight,
    RefreshCw,
    Search,
    Calendar,
    X,
    Plus,
    UploadCloud,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Link2,
    Image as ImageIcon,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    List,
    ListOrdered,
} from 'lucide-react';
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import Layout from '../../Layout'
import { useNavigate, Link } from "react-router-dom";

const { TextArea } = Input;
const { Dragger } = Upload;

export default function AddSupplierForm() {
    const [isDarkMode, setIsDarkMode] = useState(false);

    // 1. Quản lý trạng thái Đóng/Mở (Collapse) của các Section
    const [expandedSections, setExpandedSections] = useState({
        general: true,
        account: true,
        contact: true,
        description: true,
        attachment: true
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 2. Data và State cho Tài khoản ngân hàng (Dynamic Fields)
    const bankOptions = [
        { value: 'vcb', label: 'Vietcombank (VCB)' },
        { value: 'tcb', label: 'Techcombank (TCB)' },
        { value: 'mb', label: 'MB Bank' },
        { value: 'acb', label: 'ACB' }
    ];
    const gioitinhOptions = [
        { value: '1', label: 'Nam' },
        { value: '2', label: 'Nữ' },
        { value: '3', label: 'Khác' }
    ];
    const danhxungOptions = [
        { value: '1', label: 'Ông' },
        { value: '2', label: 'Bà' },
        { value: '3', label: 'Anh' },
        { value: '4', label: 'Chi' },
    ];
    const linhvucOptions = [
        { value: '1', label: 'Spa' },
        { value: '2', label: 'Bệnh viện' },
        { value: '3', label: 'Chứng khoán' },
        { value: '4', label: 'Công nghệ' },
    ];
    const [accounts, setAccounts] = useState([
        { id: 1, accountNo: '', bank: null }
    ]);

    const handleAddAccount = () => {
        setAccounts([...accounts, { id: Date.now(), accountNo: '', bank: null }]);
    };
    const handleRemoveAccount = (id) => {
        setAccounts(accounts.filter(acc => acc.id !== id));
    };

    // 3. Data và State cho Thông tin nhà cung cấp (Dynamic Fields)
    const contactTypeOptions = [
        { value: 'Facebook', label: 'Facebook' },
        { value: 'Skype', label: 'Skype' },
        { value: 'Website', label: 'Website' },
        { value: 'Zalo', label: 'Zalo' },
        { value: 'Fanpage', label: 'Fanpage' },
        { value: 'Zalo OA', label: 'Zalo OA' },
        { value: 'Địa chỉ', label: 'Địa chỉ' }
    ];
    const [contacts, setContacts] = useState([
        { id: 1, type: 'Website', detail1: '', detail2: '' },
        { id: 2, type: 'Địa chỉ', detail1: '', detail2: '' },
        { id: 3, type: 'Zalo', detail1: '', detail2: '' }
    ]);

    const handleAddContact = () => {
        setContacts([...contacts, { id: Date.now(), type: 'Địa chỉ', detail1: '', detail2: '' }]);
    };
    const handleRemoveContact = (id) => {
        setContacts(contacts.filter(c => c.id !== id));
    };
    const handleChangeContactType = (id, newType) => {
        setContacts(contacts.map(c => c.id === id ? { ...c, type: newType, detail1: '', detail2: '' } : c));
    };

    // Mock Data cho Custom Select Hàng Hóa (Hình 2)
    const productOptions = [
        { value: 'sp1', label: 'SP.XD.122024.1 - Xi măng Hà Tiên PCB40', stock: '11196 (Bao)', img: 'https://i.pravatar.cc/150?img=1' },
        { value: 'sp2', label: 'SP.XD.122024.2 - Gạch đỏ ống', stock: '17384 (Viên)', img: 'https://i.pravatar.cc/150?img=2' },
        { value: 'sp3', label: 'SP.XD.122024.3 - Gạch block bê tông', stock: '2913 (Viên)', img: 'https://i.pravatar.cc/150?img=3' },
        { value: 'sp4', label: 'SP.XD.122024.4 - Cát xây tô', stock: '185 (m3)', img: 'https://i.pravatar.cc/150?img=4' },
    ];

    // Mock Data cho Custom Select Khách Hàng (Hình 3)
    const customerOptions = [
        { value: 'c1', label: '000389 - Nguyễn Phương Thảo', subtitle: 'Liên hệ' },
        { value: 'c2', label: 'T10-05 - Công ty Vina sữa', subtitle: 'Liên hệ' },
        { value: 'c3', label: 'T10-06 - Mỹ phẩm', subtitle: 'Liên hệ' },
        { value: 'c4', label: 'T10-07 - Công Ty TNHH Giáo Dục Và Đào Tạo Vinacademy', subtitle: 'Liên hệ' },
    ];

    // Theme Config
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
            controlHeight: 40,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    // Component Custom Header (Click để Collapse)
    const SectionHeader = ({ title, sectionKey }) => (
        <div
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center gap-2 text-[#3e91ab] font-medium text-sm mb-4 mt-6 first:mt-0 cursor-pointer hover:opacity-80 transition-opacity select-none"
        >
            {expandedSections[sectionKey] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            <span>{title}</span>
        </div>
    );

    return (
        <Layout>
            <div>
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm mb-4">
                    <Link to="/supplier" className="flex items-center gap-2">
                        <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                    </Link>
                    <Link to="/supplier" className="flex items-center gap-2 text-000000e0">
                        <span>Nhà cung cấp</span>
                    </Link>
                    <span><RightOutlined className="w-4 h-4 text-[12px] text-gray-500 text-000000e0" /></span>
                    <span className="text-000000e0">Thêm mới nhà cung cấp</span>
                </div>

                <div className="flex gap-2 mb-4 flex items-center">
                    <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                        Thêm mới nhà cung cấp
                    </h2>
                </div>

                <div className={`w-full max-w-5xl mx-auto rounded-lg shadow-sm p-4 md:p-8 dark:bg-[#1f1f1f] bg-white`}>

                    {/* --- SECTION 1: THÔNG TIN CHUNG --- */}
                    <SectionHeader title="Thông tin chung" sectionKey="general" />
                    {expandedSections.general && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                            <Select defaultValue="CaNhan" options={[{ value: 'CaNhan', label: 'Cá nhân' }, { value: 'ToChuc', label: 'Tổ chức' }]} className="w-full" />
                            <Input defaultValue="NCC_2026_79" suffix={<RefreshCw size={16} className="text-gray-400 cursor-pointer hover:text-[#3e91ab]" />} />

                            <Input placeholder="Tên khách hàng *" className="md:col-span-2" />
                            <Select placeholder="Lĩnh vực" className="md:col-span-2 w-full" options={linhvucOptions} />

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:col-span-2">
                                <Select placeholder="Danh xưng" className="w-full" options={danhxungOptions} />
                                <DatePicker placeholder="Ngày sinh" className="w-full" suffixIcon={<Calendar size={16} className="text-gray-400" />} format="DD/MM/YYYY" />
                                <Select placeholder="Giới tính" className="w-full" options={gioitinhOptions} />
                            </div>

                            <Input placeholder="Số điện thoại" />
                            <Input placeholder="Email" />
                            <Input placeholder="CMND/Căn cước/HC" />
                            <DatePicker placeholder="Ngày cấp" className="w-full" suffixIcon={<Calendar size={16} className="text-gray-400" />} format="DD/MM/YYYY" />
                            <Input placeholder="Nơi cấp" suffix={<Search size={16} className="text-gray-400" />} className="md:col-span-2" />

                            <Select mode="multiple" className="md:col-span-2 w-full" placeholder="Phụ trách" defaultValue={['huan']} options={[{ value: 'huan', label: 'HQ_495 - Mr. Huấn' }]} />

                            {/* CUSTOM SELECT: HÀNG HÓA CUNG CẤP (Hình 2) */}
                            <Select
                                mode="multiple"
                                className="md:col-span-2 w-full"
                                placeholder="Hàng hóa cung cấp"
                                options={productOptions}
                                optionRender={(option) => (
                                    <div className="flex items-center gap-3 py-1">
                                        <Avatar src={option.data.img} />
                                        <div className="flex flex-col">
                                            <span className="font-medium text-gray-800 dark:text-gray-200">{option.data.label}</span>
                                            <span className="text-[11px] text-gray-500 italic">Tổng tồn kho: {option.data.stock}</span>
                                        </div>
                                    </div>
                                )}
                            />

                            {/* CUSTOM SELECT: KHÁCH HÀNG (Hình 3) */}
                            <Select
                                showSearch
                                className="md:col-span-2 w-full"
                                placeholder="Chọn khách hàng"
                                suffixIcon={<Search size={16} className="text-gray-400" />}
                                options={customerOptions}
                                optionRender={(option) => (
                                    <div className="flex flex-col py-1">
                                        <span className="font-medium text-gray-800 dark:text-gray-200">{option.data.label}</span>
                                        <span className="text-[11px] text-gray-500 italic">{option.data.subtitle}</span>
                                    </div>
                                )}
                            />
                        </div>
                    )}

                    {/* --- SECTION 2: TÀI KHOẢN --- */}
                    <SectionHeader title="Tài khoản" sectionKey="account" />
                    {expandedSections.account && (
                        <div className="animate-in slide-in-from-top-2">
                            <div className="grid grid-cols-[1fr_1fr_auto] gap-4 mb-3 items-center text-xs font-medium text-gray-500 dark:text-gray-400 px-1">
                                <span>Số tài khoản</span>
                                <span>Ngân hàng</span>
                                <span className="w-6"></span>
                            </div>

                            {/* MAP DỮ LIỆU ĐỘNG TÀI KHOẢN */}
                            {accounts.map((acc) => (
                                <div key={acc.id} className="grid grid-cols-[1fr_1fr_auto] gap-4 mb-3 items-center">
                                    <Input placeholder="Số tài khoản" />
                                    <Select
                                        placeholder="Chọn ngân hàng"
                                        options={bankOptions}
                                        className="w-full"
                                    />
                                    <X
                                        size={18}
                                        className="text-gray-400 cursor-pointer hover:text-red-500"
                                        onClick={() => handleRemoveAccount(acc.id)}
                                    />
                                </div>
                            ))}

                            {/* Nút Cộng (+) Tài khoản (Hình 4) */}
                            <button
                                onClick={handleAddAccount}
                                className="flex items-center justify-center w-8 h-8 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    )}

                    {/* --- SECTION 3: THÔNG TIN NHÀ CUNG CẤP --- */}
                    <SectionHeader title="Thông tin nhà cung cấp" sectionKey="contact" />
                    {expandedSections.contact && (
                        <div className="animate-in slide-in-from-top-2">
                            <div className="grid grid-cols-[150px_1fr_auto] gap-4 mb-3 items-center text-xs font-medium text-gray-500 dark:text-gray-400 px-1">
                                <span>Loại</span>
                                <span>Chi tiết</span>
                                <span className="w-6"></span>
                            </div>

                            {/* MAP DỮ LIỆU ĐỘNG LIÊN HỆ */}
                            {contacts.map((contact) => (
                                <div key={contact.id} className="grid grid-cols-[150px_1fr_auto] gap-4 mb-3 items-start">
                                    {/* Chọn Loại (Hình 5) */}
                                    <Select
                                        value={contact.type}
                                        onChange={(val) => handleChangeContactType(contact.id, val)}
                                        options={contactTypeOptions}
                                        className="w-full"
                                    />

                                    {/* Điều kiện Render Input dựa vào "Loại" */}
                                    {contact.type === 'Địa chỉ' ? (
                                        // Giao diện 2 input (Hình 6)
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                                            <Input placeholder="Địa chỉ, số nhà, ....." />
                                            <Input placeholder="Xã phường, quận huyện, tỉnh..." suffix={<Search size={16} className="text-gray-400" />} />
                                        </div>
                                    ) : (
                                        // Giao diện 1 input (Hình 7)
                                        <Input placeholder="Thông tin chi tiết" />
                                    )}

                                    <div className="flex items-center h-10">
                                        <X
                                            size={18}
                                            className="text-gray-400 cursor-pointer hover:text-red-500"
                                            onClick={() => handleRemoveContact(contact.id)}
                                        />
                                    </div>
                                </div>
                            ))}

                            <button
                                onClick={handleAddContact}
                                className="flex items-center justify-center w-8 h-8 rounded-full border border-[#3e91ab] text-[#3e91ab] hover:bg-[#3e91ab] hover:text-white transition-colors mt-2"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    )}

                    {/* --- SECTION 4: MÔ TẢ --- */}
                    <SectionHeader title="Mô tả" sectionKey="description" />
                    {expandedSections.description && (
                        <div className={`border rounded-lg overflow-hidden animate-in slide-in-from-top-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                            <div className={`flex flex-wrap items-center gap-1 p-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-gray-50'}`}>
                                {[Bold, Italic, Underline, Strikethrough, Link2, ImageIcon].map((Icon, idx) => (
                                    <button key={idx} className="p-1.5 text-gray-500 hover:text-[#3e91ab] hover:bg-gray-200 dark:hover:bg-gray-700 rounded"><Icon size={16} /></button>
                                ))}
                                <Divider type="vertical" className="h-6" />
                                {[AlignLeft, AlignCenter, AlignRight, AlignJustify].map((Icon, idx) => (
                                    <button key={idx} className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${idx === 3 ? 'text-[#3e91ab] bg-[#3e91ab]/10' : 'text-gray-500 hover:text-[#3e91ab]'}`}>
                                        <Icon size={16} />
                                    </button>
                                ))}
                                <Divider type="vertical" className="h-6" />
                                {[List, ListOrdered].map((Icon, idx) => (
                                    <button key={idx} className="p-1.5 text-gray-500 hover:text-[#3e91ab] hover:bg-gray-200 dark:hover:bg-gray-700 rounded"><Icon size={16} /></button>
                                ))}
                            </div>
                            <TextArea placeholder="Điền mô tả nhà cung cấp" bordered={false} autoSize={{ minRows: 4, maxRows: 8 }} className="p-4" />
                        </div>
                    )}

                    {/* --- 5. ĐÍNH KÈM --- */}
                    <SectionHeader title="Đính kèm" sectionKey="attachment" />
                    {expandedSections.attachment && (
                        /* Đổi md:w-1/2 thành lg:w-1/2 để tablet hiển thị full chiều rộng */
                        <div className="animate-in slide-in-from-top-2 mb-6 w-full lg:w-1/2">
                            <Dragger className={`bg-transparent`} style={{ padding: '20px', border: `1px dashed ${isDarkMode ? '#4b5563' : '#d1d5db'}` }}>
                                {/* Thêm flex-col cho mobile và sm:flex-row cho màn hình to hơn */}
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-[#3e91ab]/10 flex items-center justify-center shrink-0">
                                        <UploadCloud size={20} className="text-[#3e91ab]" />
                                    </div>
                                    <div className="text-center sm:text-left">
                                        <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                                            Kéo thả file vào đây để tải lên hoặc
                                        </p>
                                        {/* Thêm flex-wrap để chống tràn nút */}
                                        <div className="flex flex-wrap justify-center sm:justify-start gap-2">
                                            <Button type="primary" size="small" style={{ backgroundColor: '#3e91ab' }}>
                                                CHỌN TỪ MÁY
                                            </Button>
                                            <Button size="small" icon={<UploadCloud size={14} />}>
                                                CHỌN TỪ CLOUD
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Dragger>
                        </div>
                    )}

                </div>

                <div className="w-full max-w-5xl mx-auto mt-6 flex gap-3">
                    <Button type="primary" size="large" style={{ backgroundColor: '#3e91ab', fontWeight: 500 }}>
                        CẬP NHẬT
                    </Button>
                    <Button size="large" className="font-medium">
                        HỦY BỎ
                    </Button>
                </div>
            </div>
        </Layout>
    );
}